import type { ConfirmDialogMessages } from 'containers/ConfirmDialog/ConfirmDialog';
import { useSimpleConfirmDialog } from 'containers/ConfirmDialog/useSimpleConfirmDialog';
import type { ReactNode } from 'react';
import { useState } from 'react';
import type {
  Control,
  FieldPath,
  FieldValues,
  UseFormSetValue,
} from 'react-hook-form';
import { useWatch } from 'react-hook-form';
import { useNotifications } from 'utils/hooks/useNotifications';

export type UseAimRowActionsParams = {
  fieldName: FieldPath<FieldValues>;
  control: Control<FieldValues>;
  setValue: UseFormSetValue<FieldValues>;
  isReferenced: (id: string) => string[];
  deleteConfirmLabel: (count: number) => ConfirmDialogMessages;
  blockedDeleteLabel: (
    count: number,
    referencingTotal: number,
  ) => ConfirmDialogMessages;
};

export type UseAimRowActionsResult<TEntity> = {
  editedItem: TEntity | 'new' | null;
  handleAdd: () => void;
  handleEdit: (row: TEntity) => void;
  handleCloseDialog: () => void;
  handleSubmitDialog: (entity: TEntity) => void;
  handleDelete: (id: string) => Promise<void>;
  handleBulkDelete: (selectedIds: Set<string>) => Promise<boolean>;
  confirmDeleteDialogRenderer: () => ReactNode;
};

export const useAimRowActions = <TEntity extends { id: string }>(
  params: UseAimRowActionsParams,
): UseAimRowActionsResult<TEntity> => {
  const items = (useWatch({
    control: params.control,
    name: params.fieldName,
  }) ?? []) as TEntity[];
  const [editedItem, setEditedItem] = useState<TEntity | 'new' | null>(null);
  const confirmDeleteDialog = useSimpleConfirmDialog();
  const notifications = useNotifications();

  const handleAdd = () => setEditedItem('new');
  const handleEdit = (row: TEntity) => setEditedItem(row);
  const handleCloseDialog = () => setEditedItem(null);

  const handleSubmitDialog = (entity: TEntity) => {
    if (editedItem === 'new') {
      params.setValue(params.fieldName, [...items, entity], {
        shouldDirty: true,
      });
      setEditedItem(null);
      return;
    }
    if (!items.some((i) => i.id === entity.id)) {
      notifications.showNotification({
        type: 'danger',
        message:
          'The record was deleted while the editor was open. Your changes were discarded.',
      });
      setEditedItem(null);
      return;
    }
    params.setValue(
      params.fieldName,
      items.map((i) => (i.id === entity.id ? entity : i)),
      { shouldDirty: true },
    );
    setEditedItem(null);
  };

  const handleDelete = async (id: string) => {
    const referencing = params.isReferenced(id);
    if (referencing.length > 0) {
      await confirmDeleteDialog.show(
        params.blockedDeleteLabel(1, referencing.length),
      );
      return;
    }
    if (await confirmDeleteDialog.show(params.deleteConfirmLabel(1))) {
      params.setValue(
        params.fieldName,
        items.filter((i) => i.id !== id),
        { shouldDirty: true },
      );
    }
  };

  const handleBulkDelete = async (
    selectedIds: Set<string>,
  ): Promise<boolean> => {
    const referenced = [...selectedIds].filter(
      (id) => params.isReferenced(id).length > 0,
    );
    if (referenced.length > 0) {
      const totalRefs = referenced.flatMap((id) =>
        params.isReferenced(id),
      ).length;
      await confirmDeleteDialog.show(
        params.blockedDeleteLabel(referenced.length, totalRefs),
      );
      return false;
    }
    if (
      await confirmDeleteDialog.show(
        params.deleteConfirmLabel(selectedIds.size),
      )
    ) {
      params.setValue(
        params.fieldName,
        items.filter((i) => !selectedIds.has(i.id)),
        { shouldDirty: true },
      );
      return true;
    }
    return false;
  };

  return {
    editedItem,
    handleAdd,
    handleEdit,
    handleCloseDialog,
    handleSubmitDialog,
    handleDelete,
    handleBulkDelete,
    confirmDeleteDialogRenderer: confirmDeleteDialog.render,
  };
};
